import { SUPPORT_SESSION_FRAGMENT_KEY } from '@yourstore/domain';
import { AuthError } from './provider';
import type { AuthProvider, StaffUser, SupportSessionState } from './provider';

/**
 * The support session on the device — "see what she sees" (`@yourstore/domain`'s
 * `auth.ts` for the shape; `core-api`'s `auth/support.ts` for what verifies it).
 *
 * The third `AuthProvider`, and the narrowest: it holds **one fixed token**
 * the console minted, presents it until the token's own `exp`, and then stops.
 * There is no sign-in (the console is the sign-in), no refresh (fifteen
 * minutes is the whole of it), and no persistence beyond this tab
 * (`sessionStorage`, so a reload inside the window keeps the look and closing
 * the tab ends it — the opposite of the staff seat's
 * `browserLocalPersistence`, because an operator's laptop is not a counter
 * iPad and a look that outlived its tab would be a standing window into a
 * book).
 *
 * ## The fragment, and why it is read here and scrubbed at once
 *
 * The console hands the token over in the URL **fragment**
 * (`#support_session=…`), never the query string: a query string is sent to
 * the server and would keep the credential verbatim in the load balancer's,
 * Cloud Run's and Hosting's request logs — the class of leak obligation K16
 * exists to prevent. A fragment never leaves the browser. `readSupportSession`
 * reads it once at boot, replaces the address (`history.replaceState`) so it
 * sits neither in the bar nor in history, and moves it to `sessionStorage`.
 * `SignIn.tsx`'s prefill is the precedent and the same discipline.
 *
 * ## What this class decides, and what it does not
 *
 * It decides **when the look is over** — `token()` answers `null` past
 * `expiresAt`, `current()` answers `null` with it, and the shell draws the
 * ended screen (`App.tsx`). It decides nothing about what the look may do:
 * read-only is `core-api`'s refusal on every write, and the outbox guard in
 * `actions.ts` is the device-side echo of it. The claims it reads off the token
 * are read **without verification**, on purpose — this is a display decision
 * (which name goes on the banner, when the clock says it ends), and the only
 * verifier that matters is the server's.
 */

const STORAGE_KEY = 'yourstore.support-session';

export interface SupportSessionClaimsView {
  tenantId: string;
  operator: string;
  /** Seconds since the epoch, as the token carries it. */
  exp: number;
  uid: string;
}

/**
 * The two claims the banner needs and the one the clock needs, decoded from
 * the payload segment. Not a verification and not pretending to be one.
 */
export function decodeSupportSession(token: string): SupportSessionClaimsView | null {
  try {
    const segment = token.split('.')[1];
    if (segment === undefined || segment === '') return null;
    const json = base64UrlDecode(segment);
    const payload: unknown = JSON.parse(json);
    if (typeof payload !== 'object' || payload === null) return null;
    const p = payload as {
      sub?: unknown;
      tenant_id?: unknown;
      exp?: unknown;
      support?: { operator?: unknown } | undefined;
    };
    if (
      typeof p.sub !== 'string' ||
      typeof p.tenant_id !== 'string' ||
      typeof p.exp !== 'number' ||
      typeof p.support?.operator !== 'string'
    ) {
      return null;
    }
    return { uid: p.sub, tenantId: p.tenant_id, exp: p.exp, operator: p.support.operator };
  } catch {
    return null;
  }
}

/**
 * The token for this tab, if the console handed one over: the fragment first
 * (and it is scrubbed the moment it is read), then what an earlier boot of this
 * same tab stashed. `null` is every ordinary open of the app.
 */
export function readSupportSession(options: {
  location: { hash: string; pathname: string; search: string };
  history: { replaceState: (data: unknown, unused: string, url: string) => void };
  storage: Storage | null;
}): string | null {
  try {
    const raw = options.location.hash;
    if (raw.includes(`${SUPPORT_SESSION_FRAGMENT_KEY}=`)) {
      const params = new URLSearchParams(raw.replace(/^#/, ''));
      const token = params.get(SUPPORT_SESSION_FRAGMENT_KEY) ?? '';
      // Scrubbed whether or not it parses: a malformed credential in the
      // address bar is still a credential in the address bar.
      options.history.replaceState(null, '', options.location.pathname + options.location.search);
      if (token !== '' && decodeSupportSession(token) !== null) {
        options.storage?.setItem(STORAGE_KEY, token);
        return token;
      }
      return null;
    }
    const stashed = options.storage?.getItem(STORAGE_KEY) ?? null;
    return stashed !== null && decodeSupportSession(stashed) !== null ? stashed : null;
  } catch {
    return null;
  }
}

export class SupportSessionAuthProvider implements AuthProvider {
  readonly kind = 'support' as const;
  private readonly tokenValue: string;
  private readonly claims: SupportSessionClaimsView;
  private readonly storage: Storage | null;
  private readonly now: () => number;
  private readonly listeners = new Set<(user: StaffUser | null) => void>();
  private endedByHand = false;

  constructor(options: { token: string; storage?: Storage | null; now?: () => number }) {
    const claims = decodeSupportSession(options.token);
    if (claims === null) {
      throw new Error('SupportSessionAuthProvider needs a token shaped like a support session.');
    }
    this.tokenValue = options.token;
    this.claims = claims;
    this.storage = options.storage === undefined ? safeSessionStorage() : options.storage;
    this.now = options.now ?? Date.now;
  }

  /** The provider's own view — the banner's two facts and whether it is over. */
  readonly support = (): SupportSessionState => ({
    tenantId: this.claims.tenantId,
    operator: this.claims.operator,
    expiresAt: new Date(this.claims.exp * 1000).toISOString(),
    ended: this.ended(),
  });

  current(): StaffUser | null {
    return this.ended() ? null : { uid: this.claims.uid, email: null };
  }

  async signIn(): Promise<void> {
    throw new AuthError(
      'unavailable',
      'A support view cannot sign in. Mint another look from the console.',
    );
  }

  /** "End support view" — and what the expiry timer calls. */
  async signOut(): Promise<void> {
    this.endedByHand = true;
    this.storage?.removeItem(STORAGE_KEY);
    for (const listener of this.listeners) listener(null);
  }

  async token(): Promise<string | null> {
    return this.ended() ? null : this.tokenValue;
  }

  onChange(listener: (user: StaffUser | null) => void): () => void {
    this.listeners.add(listener);
    listener(this.current());
    return () => {
      this.listeners.delete(listener);
    };
  }

  private ended(): boolean {
    return this.endedByHand || this.claims.exp * 1000 <= this.now();
  }
}

function base64UrlDecode(segment: string): string {
  const padded =
    segment.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (segment.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

function safeSessionStorage(): Storage | null {
  try {
    return globalThis.sessionStorage;
  } catch {
    return null;
  }
}
